import React, { useState, useEffect } from "react";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400&family=Source+Serif+4:ital,opsz,wght@0,8..60,300;0,8..60,400;0,8..60,600;1,8..60,300&family=Space+Mono:wght@400;700&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --ink: #0f0e0c;
    --paper: #f5f0e8;
    --paper-dark: #ede7d6;
    --steel: #2c3e50;
    --steel-mid: #4a6274;
    --accent: #b5451b;
    --accent-light: #e8623a;
    --gold: #c9943a;
    --gold-light: #f0c96e;
    --rule: #d4c9b0;
  }

  .article-root {
    background: var(--paper);
    min-height: 100vh;
    font-family: 'Source Serif 4', Georgia, serif;
    color: var(--ink);
    position: relative;
    overflow-x: hidden;
  }

  /* HEADER */
  .article-header {
    background: var(--steel);
    padding: 0;
    position: relative;
    overflow: hidden;
  }

  .header-noise {
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.06'/%3E%3C/svg%3E");
    opacity: 0.4;
    pointer-events: none;
  }

  .header-stripe {
    height: 4px;
    background: linear-gradient(90deg, var(--accent) 0%, var(--gold) 50%, var(--accent) 100%);
  }

  .header-inner {
    max-width: 900px;
    margin: 0 auto;
    padding: 48px 40px 40px;
    position: relative;
    z-index: 1;
  }

  .journal-label {
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--gold-light);
    opacity: 0.8;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .journal-label::before,
  .journal-label::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--gold);
    opacity: 0.3;
  }

  .article-title {
    font-family: 'Playfair Display', serif;
    font-size: clamp(24px, 4vw, 44px);
    font-weight: 900;
    line-height: 1.15;
    color: #fff;
    margin-bottom: 28px;
    letter-spacing: -0.02em;
  }

  .article-title .accent-word {
    color: var(--gold-light);
    font-style: italic;
  }

  .author-block {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px 0;
    border-top: 1px solid rgba(201,148,58,0.3);
  }

  .author-avatar {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--accent), var(--gold));
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Playfair Display', serif;
    font-size: 18px;
    font-weight: 700;
    color: #fff;
    flex-shrink: 0;
  }

  .author-info {
    display: flex;
    flex-direction: column;
  }

  .author-name {
    font-family: 'Playfair Display', serif;
    font-size: 15px;
    font-weight: 700;
    font-style: italic;
    color: #fff;
  }

  .author-role {
    font-family: 'Space Mono', monospace;
    font-size: 9px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--gold-light);
    opacity: 0.7;
    margin-top: 2px;
  }

  /* HERO IMAGE */
  .hero-image-section {
    position: relative;
    width: 100%;
    overflow: hidden;
    background: var(--steel);
  }

  .hero-image-section img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0.75;
    filter: sepia(20%) contrast(1.1);
  }

  .hero-caption {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 20px 40px 16px;
    background: linear-gradient(to top, rgba(15,14,12,0.85) 0%, transparent 100%);
  }

  .hero-caption-text {
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: var(--gold-light);
    opacity: 0.9;
  }

  /* MAIN BODY */
  .article-body {
    max-width: 900px;
    margin: 0 auto;
    padding: 0 40px 80px;
  }

  /* ABSTRACT */
  .abstract-block {
    margin: 48px 0 40px;
    padding: 32px 36px;
    background: var(--paper-dark);
    border-left: 4px solid var(--accent);
    position: relative;
    box-shadow: 4px 4px 0 rgba(181,69,27,0.08);
  }

  .abstract-label {
    font-family: 'Space Mono', monospace;
    font-size: 9px;
    letter-spacing: 0.35em;
    text-transform: uppercase;
    color: var(--accent);
    margin-bottom: 14px;
    font-weight: 700;
  }

  .abstract-text {
    font-size: 15px;
    line-height: 1.8;
    color: var(--steel);
    font-style: italic;
    font-weight: 300;
  }

  /* SECTION */
  .section {
    margin-bottom: 40px;
    padding-bottom: 40px;
    border-bottom: 1px solid var(--rule);
    animation: fadeUp 0.5s ease both;
  }

  .section:last-child {
    border-bottom: none;
  }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(16px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .section-number {
    font-family: 'Space Mono', monospace;
    font-size: 11px;
    letter-spacing: 0.15em;
    color: var(--gold);
    font-weight: 700;
    margin-bottom: 6px;
  }

  .section-title {
    font-family: 'Playfair Display', serif;
    font-size: clamp(18px, 2.5vw, 26px);
    font-weight: 700;
    color: var(--steel);
    margin-bottom: 18px;
    line-height: 1.25;
    border-bottom: 2px solid var(--rule);
    padding-bottom: 10px;
  }

  .section-body {
    font-size: 16px;
    line-height: 1.85;
    color: #2a2a2a;
    font-weight: 300;
  }

  .section-body p {
    margin-bottom: 14px;
  }

  .section-body strong {
    color: var(--steel);
    font-weight: 600;
  }

  /* UNDERLINED KEY PHRASE */
  .key-phrase {
    text-decoration: underline;
    text-decoration-color: var(--gold);
    text-underline-offset: 3px;
    font-weight: 600;
    color: var(--steel);
  }

  /* BULLET LIST */
  .bullet-list {
    list-style: none;
    margin: 14px 0 14px 8px;
    padding: 0;
  }

  .bullet-list li {
    padding: 6px 0 6px 24px;
    position: relative;
    font-size: 15.5px;
    line-height: 1.7;
    color: #2a2a2a;
    font-weight: 300;
  }

  .bullet-list li::before {
    content: '';
    position: absolute;
    left: 0;
    top: 15px;
    width: 10px;
    height: 2px;
    background: var(--accent);
  }

  /* STAT CALLOUT */
  .stat-callout {
    margin: 32px 0;
    padding: 24px 28px;
    background: var(--steel);
    color: #fff;
    display: flex;
    align-items: center;
    gap: 24px;
    position: relative;
    overflow: hidden;
  }

  .stat-callout::before {
    content: '';
    position: absolute;
    right: -20px;
    top: -20px;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: rgba(201,148,58,0.12);
  }

  .stat-num {
    font-family: 'Playfair Display', serif;
    font-size: 42px;
    font-weight: 900;
    color: var(--gold-light);
    line-height: 1;
    flex-shrink: 0;
  }

  .stat-desc {
    font-family: 'Source Serif 4', serif;
    font-size: 14px;
    line-height: 1.6;
    color: rgba(255,255,255,0.85);
    font-style: italic;
  }

  /* PULL QUOTE */
  .pull-quote {
    margin: 36px -12px;
    padding: 28px 32px;
    border-top: 3px solid var(--gold);
    border-bottom: 3px solid var(--gold);
    text-align: center;
  }

  .pull-quote-text {
    font-family: 'Playfair Display', serif;
    font-size: clamp(16px, 2vw, 21px);
    font-style: italic;
    line-height: 1.6;
    color: var(--steel);
  }

  /* TABLE OF CONTENTS */
  .toc-sidebar {
    background: var(--paper-dark);
    border: 1px solid var(--rule);
    padding: 24px 28px;
    margin: 0 0 44px;
  }

  .toc-title {
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--steel-mid);
    margin-bottom: 14px;
    font-weight: 700;
  }

  .toc-list {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .toc-item {
    display: flex;
    align-items: baseline;
    gap: 8px;
    font-size: 13px;
    color: var(--steel);
    cursor: pointer;
    padding: 3px 0;
    transition: color 0.2s;
  }

  .toc-item:hover {
    color: var(--accent);
  }

  .toc-num {
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    color: var(--gold);
    flex-shrink: 0;
  }

  /* FOOTER */
  .article-footer {
    background: var(--steel);
    padding: 32px 40px;
    text-align: center;
  }

  .footer-logo {
    font-family: 'Space Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--gold-light);
    opacity: 0.6;
  }

  /* RESPONSIVE */
  @media (max-width: 600px) {
    .header-inner, .article-body, .hero-caption, .article-footer { padding-left: 20px; padding-right: 20px; }
    .abstract-block { padding: 22px 20px; }
    .stat-callout { flex-direction: column; gap: 8px; text-align: center; }
    .hero-image-section { height: 220px; }
  }
`;

const sections = [
  {
    num: "01",
    title: "Introduction",
    content: (
      <>
        <p>
          Steel remains the cornerstone of modern industrial and economic development. It is an indispensable material for building infrastructure, transportation networks, machinery, construction projects, and increasingly for renewable energy systems such as wind turbines, solar installations, and electric mobility infrastructure. As economies expand and urbanization accelerates, global demand for steel continues to grow, reinforcing its importance in supporting economic development and industrial progress.
        </p>
        <p>
          However, steel production is also recognized as one of the most energy-intensive and carbon-emitting industrial sectors. Globally, the iron and steel industry accounts for nearly 7–9 percent of total greenhouse gas emissions, largely due to the reliance on fossil fuels in conventional steelmaking processes. The challenge facing the industry today is therefore twofold: meeting rising steel demand while simultaneously reducing its environmental footprint and aligning with global climate commitments.
        </p>
        <p>
          Historically, steel production has been dominated by the Blast Furnace–Basic Oxygen Furnace (BF–BOF) route, which relies on coke derived from coal as the primary reducing agent for converting iron ore into molten iron. While this process has supported large-scale steel production for decades, it is also highly carbon intensive, generating approximately 2–2.3 tonnes of carbon dioxide (CO₂) per tonne of steel produced.
        </p>
        <p>
          In contrast, the secondary steel sector, particularly the Induction Furnace (IF) route, offers a comparatively low-emission alternative for steel production. Induction furnaces primarily utilize recycled steel scrap and other metallic inputs as raw materials and rely on electrical energy rather than the direct combustion of fossil fuels. As a result, the steel melting stage in induction furnaces produces minimal direct carbon emissions, making it significantly more environmentally favourable than conventional coal-based steelmaking routes.
        </p>
        <p>
          In countries such as India, where the secondary steel sector plays a vital role in meeting domestic steel demand, induction furnace-based steelmaking has gained considerable importance. India hosts one of the world’s largest networks of induction furnace steel units, particularly within regional steel clusters that support local infrastructure and construction needs. These units not only contribute significantly to national steel production but also promote the recycling of steel scrap, thereby strengthening the circular economy within the steel industry.
        </p>
        <p>
          By utilizing recycled raw materials, improving operational energy efficiency, and integrating renewable electricity sources, the induction furnace route presents a practical, scalable, and economically viable pathway toward low-carbon steel production.
        </p>
      </>
    ),
  },
  {
    num: "02",
    title: "Overview of the Induction Furnace Steelmaking Process",
    content: (
      <>
        <p>
          The Induction Furnace (IF) is an electrically operated melting unit that utilizes the principle of electromagnetic induction to generate heat and melt metallic charge materials. In this process, alternating current passes through a water-cooled copper coil surrounding the furnace crucible, creating a rapidly changing magnetic field. This magnetic field induces eddy currents within the metallic charge placed inside the crucible. The electrical resistance of the metal converts these currents into heat, resulting in efficient and uniform melting of the charge material.
        </p>
        <p>
          Induction furnace technology is widely used in the secondary steel sector due to its operational flexibility, relatively lower capital investment, and capability to produce a wide range of steel grades using recycled raw materials.
        </p>
        <p>
          <strong>Typical Process Flow in Induction Furnace Steelmaking</strong>
        </p>
        <ul className="bullet-list">
          <li>Scrap Collection and Preparation: Steel scrap, sponge iron, and other metallic inputs are collected, sorted, and processed to ensure proper charge composition and removal of impurities.</li>
          <li>Charging of Raw Materials: Prepared scrap and metallic inputs are charged into the furnace crucible in a controlled sequence to facilitate efficient melting.</li>
          <li>Melting through Electromagnetic Induction: Electric current supplied to the induction coil generates electromagnetic energy that melts the metallic charge rapidly and uniformly.</li>
          <li>Alloying and Chemical Adjustment: During the melting stage, alloying elements and additives are introduced to achieve the desired chemical composition and steel grade.</li>
          <li>Slag Formation and Removal: Impurities combine with fluxes to form slag, which floats on the surface of molten steel and is removed to improve metal purity.</li>
          <li>Tapping and Casting: Refined molten steel is tapped into ladles and transferred to casting units such as continuous casting machines or ingot moulds for solidification.</li>
        </ul>
        <p>
          <strong>Operational Advantages</strong>
        </p>
        <p>
          Unlike conventional blast furnace operations, induction furnaces do not require coke combustion or the chemical reduction of iron ore. Instead, the process primarily melts recycled steel scrap and metallic inputs, significantly reducing direct carbon emissions and energy consumption associated with primary steel production.
        </p>
        <p>
          Furthermore, induction furnaces operate without open flames or electric arcs, resulting in cleaner operations, improved workplace safety, and precise temperature control.
        </p>
      </>
    ),
  },
  {
    num: "03",
    title: "Carbon Emissions in Different Steelmaking Routes",
    content: (
      <>
        <p>
          Steel can be produced through several technological routes, each with different raw materials, energy sources, and environmental impacts. The major steelmaking routes include:
        </p>
        <ul className="bullet-list">
          <li>Blast Furnace – Basic Oxygen Furnace (BF–BOF)</li>
          <li>Direct Reduced Iron – Electric Arc Furnace (DRI–EAF)</li>
          <li>Scrap-based Electric Arc Furnace (EAF)</li>
          <li>Scrap-based Induction Furnace (IF)</li>
        </ul>
        <p>
          <strong>3.1 Blast Furnace – Basic Oxygen Furnace Route</strong><br />
          The BF–BOF route remains the dominant steelmaking technology worldwide. In this process, iron ore is reduced in a blast furnace using coke derived from coal, producing molten pig iron. This molten iron is then refined in the Basic Oxygen Furnace to produce steel. However, this route is also the most carbon-intensive, generating approximately 2.0–2.3 tonnes of CO₂ per tonne of steel due to the combustion of coke and other fossil fuels.
        </p>
        <p>
          <strong>3.2 Direct Reduced Iron – Electric Arc Furnace Route</strong><br />
          In the DRI–EAF route, iron ore is reduced to sponge iron using natural gas or coal-based reducing agents. The resulting sponge iron is then melted in an Electric Arc Furnace along with steel scrap. This route generally produces 1.2–1.5 tonnes of CO₂ per tonne of steel, which is lower than the BF–BOF route but still significant.
        </p>
        <p>
          <strong>3.3 Scrap-Based Electric Arc Furnace Route</strong><br />
          The Electric Arc Furnace route primarily relies on recycled steel scrap as the main raw material. Electric arcs generated between graphite electrodes and the metallic charge produce the heat required for melting. This route typically generates 0.4–0.8 tonnes of CO₂ per tonne of steel, depending on electricity sources.
        </p>
        <p>
          <strong>3.4 Scrap-Based Induction Furnace Route</strong><br />
          The Induction Furnace route relies on electromagnetic induction to melt steel scrap and metallic inputs. Direct emissions from the melting process are extremely low since no fossil fuels are burned. The emission intensity typically ranges between 0.35–0.7 tonnes of CO₂ per tonne of steel, largely depending on the electricity generation mix.
        </p>
        <p>
          Scrap-based routes such as EAF and IF therefore represent the most immediate opportunity for reducing emissions in steel production.
        </p>
      </>
    ),
  },
  {
    num: "04",
    title: "Environmental Advantages of Induction Furnace Steelmaking",
    content: (
      <>
        <p>
          Induction furnace-based steelmaking provides several environmental benefits compared with traditional coal-based production methods.
        </p>
        <ul className="bullet-list">
          <li><strong>Lower Carbon Emissions:</strong> The absence of fossil fuel combustion significantly reduces direct greenhouse gas emissions.</li>
          <li><strong>Efficient Recycling of Steel Scrap:</strong> Steel scrap serves as the primary raw material, supporting circular economy principles and reducing the need for mining iron ore.</li>
          <li><strong>Reduced Air Pollution:</strong> Induction furnaces produce lower levels of particulate matter, sulphur oxides, and nitrogen oxides.</li>
          <li><strong>Lower Infrastructure Requirements:</strong> Induction furnace plants require smaller land areas and less complex infrastructure compared to integrated steel plants.</li>
          <li><strong>Operational Flexibility:</strong> The process allows producers to adjust charge composition and production levels quickly.</li>
          <li><strong>Compatibility with Renewable Energy:</strong> Induction furnaces operate entirely on electricity, enabling integration with renewable energy sources such as solar and wind power.</li>
        </ul>
      </>
    ),
  },
  {
    num: "05",
    title: "Future Pathways for Low-Carbon Steel Production through the Induction Furnace Route",
    content: (
      <>
        <p>
          While induction furnaces already demonstrate relatively low emissions, further improvements can enhance their sustainability performance.
        </p>
        <ul className="bullet-list">
          <li><strong>Renewable Energy Integration:</strong> Using solar, wind, and other renewable power sources can significantly reduce indirect emissions associated with electricity consumption.</li>
          <li><strong>Organized Scrap Recycling Systems:</strong> Developing structured scrap collection and processing infrastructure can ensure consistent supply of high-quality scrap.</li>
          <li><strong>Technological Modernization:</strong> Advancements in furnace design, automation, and digital monitoring systems can improve efficiency and reduce energy consumption.</li>
          <li><strong>Green Steel Certification:</strong> Developing standardized carbon accounting systems can help scrap-based steel producers demonstrate their environmental advantages.</li>
          <li><strong>Policy Support and Industry Collaboration:</strong> Government incentives, research collaboration, and industry partnerships will be essential to accelerate the transition toward low-carbon steelmaking.</li>
        </ul>
      </>
    ),
  },
  {
    num: "06",
    title: "Conclusion",
    content: (
      <>
        <p>
          The global steel industry faces the critical challenge of balancing increasing demand for steel with the urgent need to reduce greenhouse gas emissions. While traditional steelmaking routes remain heavily dependent on fossil fuels, the induction furnace route provides a practical and scalable pathway toward low-carbon steel production.
        </p>
        <p>
          By utilizing recycled steel scrap, minimizing direct emissions, and operating through electrically powered melting processes, induction furnace-based steelmaking significantly reduces the environmental impact associated with conventional ironmaking methods. When combined with renewable energy integration, improved scrap management systems, and continuous technological modernization, the induction furnace route can play a pivotal role in advancing sustainable steel production.
        </p>
        <p>
          For emerging economies such as India, where the secondary steel sector contributes significantly to overall steel output, strengthening the induction furnace ecosystem presents a strategic opportunity to align industrial growth with climate commitments. Through coordinated efforts involving policy support, technological innovation, and environmental stewardship, induction furnace-based steelmaking can contribute meaningfully to achieving long-term decarbonization goals while ensuring the continued growth and resilience of the steel industry.
        </p>
      </>
    ),
  },
];

const tocItems = [
  { num: "01", label: "Introduction" },
  { num: "02", label: "Overview of the Induction Furnace Steelmaking Process" },
  { num: "03", label: "Carbon Emissions in Different Steelmaking Routes" },
  { num: "04", label: "Environmental Advantages of Induction Furnace Steelmaking" },
  { num: "05", label: "Future Pathways for Low-Carbon Steel Production through the Induction Furnace Route" },
  { num: "06", label: "Conclusion" },
];

const Article5 = () => {
  const [loaded, setLoaded] = useState(false);
  
    useEffect(() => {
      setLoaded(true);
    }, []);
  
    const scrollTo = (idx) => {
      const el = document.getElementById(`section-${idx}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
  
    return (
      <div className="article-root">
        <style>{styles}</style>
  
        {/* HEADER */}
        <header className="article-header">
          <div className="header-noise" />
          <div className="header-stripe" />
          <div className="header-inner">
            <div className="journal-label">
              AIIFA Research Publication · April 2026 · Sustainable Steel Series
            </div>
            <h1 className="article-title">
              Sustainable Steel: Pathways to <span className="accent-word">Reduced Emissions</span> through the Induction Furnace Route
            </h1>
            <div className="author-block">
              <div className="author-avatar">YM</div>
              <div className="author-info">
                <span className="author-name">Yogesh Mandhani</span>
                <span className="author-role">
                  President, AIIFA Sustainable Steel Manufacturers Association
                </span>
              </div>
            </div>
          </div>
        </header>
  
        {/* HERO IMAGE */}
        <div className="hero-image-section">
          <img
            src="https://res.cloudinary.com/dxvbzgdgw/image/upload/q_auto/f_auto/v1776058227/WhatsApp_Image_2026-04-10_at_16.04.00_dulemr.jpg"
            alt="Induction furnace steelmaking facility"
          />
          <div className="hero-caption">
            <span className="hero-caption-text">
              Induction Furnace Route · Low-carbon &amp; Sustainable Steel Production
            </span>
          </div>
        </div>
  
        {/* BODY */}
        <main className="article-body">
          {/* ABSTRACT */}
          <div className="abstract-block">
            <div className="abstract-label">Abstract</div>
            <p className="abstract-text">
              The steel industry forms the backbone of modern economic development but contributes significantly to global greenhouse gas emissions. This article examines the environmental advantages of the induction furnace route, which utilizes electrical energy and steel scrap instead of coal-based processes. It compares carbon emissions across major steelmaking routes and highlights how scrap-based induction furnace steelmaking supports circular economy principles. With renewable energy integration and technological modernization, the induction furnace route offers a practical pathway for sustainable and low-carbon steel production in India and globally.
            </p>
          </div>
  
          {/* STAT CALLOUT */}
          <div className="stat-callout">
            <div className="stat-num">0.35–0.7</div>
            <div className="stat-desc">
              tonnes of CO₂ per tonne of steel from the scrap-based Induction Furnace route — significantly lower than the traditional BF-BOF route (2.0–2.3 t CO₂/t steel).
            </div>
          </div>
  
          {/* TABLE OF CONTENTS */}
          <div className="toc-sidebar">
            <div className="toc-title">Contents</div>
            <ul className="toc-list">
              {tocItems.map((item, i) => (
                <li key={i} className="toc-item" onClick={() => scrollTo(i)}>
                  <span className="toc-num">{item.num}</span>
                  <span>{item.label}</span>
                </li>
              ))}
            </ul>
          </div>
  
          {/* PULL QUOTE */}
          <div className="pull-quote">
            <p className="pull-quote-text">
              "The induction furnace route provides a practical, scalable, and economically viable pathway toward low-carbon steel production."
            </p>
          </div>
  
          {/* SECTIONS */}
          {sections.map((sec, idx) => (
            <section
              key={idx}
              id={`section-${idx}`}
              className="section"
              style={{ animationDelay: `${idx * 0.05}s` }}
            >
              <div className="section-number">Section {sec.num}</div>
              <h2 className="section-title">{sec.title}</h2>
              <div className="section-body">{sec.content}</div>
            </section>
          ))}
        </main>

        {/* FOOTER */}
        <footer className="article-footer">
          <div className="footer-logo">
            AIIFA Sustainable Steel Manufacturers Association © 2026
          </div>
        </footer>
      </div>
    );
  };

export default Article5;